import { arrayToMap, groupBy, min, nonNullable, partitionBy, sortBy, without } from 'lib/array/arrayUtils'
import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'

export interface RateView {
  price: number;
  boardCode: App.RateBoardCode;
  type: 'LPC' | 'bedbank';
  lpcRate: OfferPackageOptionView | undefined;
  refundable: boolean;
  bedbankRate: {
    rate: App.BedbankRate;
    view: App.BedbankOfferPackageView;
  } | undefined,
}

export function getCombinedRates(
  packageViews: Array<OfferPackageOptionView>,
  bedbankViews: Array<App.BedbankOfferPackageView>,
  availableOptions: Array<OfferPackageOptionView>,
  options: {
    roomRateId?: string;
    isHighlighted?: boolean;
    previewMode?: boolean;
    showAll?: boolean;
    utmContent?: string | null;
  }): Array<RateView> {
  const { isHighlighted, previewMode, roomRateId, showAll, utmContent } = options
  const roomFitsCapacity =
    packageViews.some(view => view.capacityValid) ||
    bedbankViews.some(view => view.capacityValid)

  let rateOptionList = availableOptions
  if (isHighlighted && roomRateId && availableOptions.length >= 1) {
    /**
      * Modifying the rateOption array so we have the highlightedRateOption on top
      **/
    const option = availableOptions.find((view) => view.package.roomRate?.id === roomRateId)
    if (option) {
      rateOptionList = [option, ...without(availableOptions, option)]
    } else if (utmContent == 'gha-lpc') {
      // if roomRateId is associated with user currency (eg: cb4ebd70-33d1-11ed-9609-ffb70b8fec99_USD ) remove the currency and extract the rate id for comparison
      const baseRateId = roomRateId?.split('_')[0]
      const option = availableOptions.find((view) => view.package.roomRate?.id === baseRateId)
      if (option) {
        rateOptionList = [option, ...without(availableOptions, option)]
      }
    }
  }

  let bedbankViewRateRecords: Array<{ rate: App.BedbankRate, view: App.BedbankOfferPackageView}> = []
  if (roomFitsCapacity) {
    bedbankViewRateRecords = bedbankViews.flatMap(view => view.rates.map(rate => ({
      rate,
      view,
    })))
  }

  // now we combine our LPC and bedbank rates
  const allRates: Array<RateView> = rateOptionList.map((rate): RateView => ({
    price: rate.memberPricing?.price || rate.pricing?.price,
    boardCode: rate.package.boardCode ?? 'LE',
    refundable: rate.refundable,
    type: 'LPC',
    lpcRate: rate,
    bedbankRate: undefined,
  }))

  if (previewMode) {
    // preview mode on property pages doesn't include bedbank rates and doesn't do any
    // any extra de-duping. Preview users want to see *all* (LPC) rates
    // those without a price of 0 will be the placed at the end
    return sortBy(allRates, (rate) => rate.price || Number.MAX_SAFE_INTEGER, 'asc')
  }

  allRates.push(...bedbankViewRateRecords.map((rate): RateView => ({
    price: (rate.rate.totals.memberTotal || rate.rate.totals.total) ?? 0,
    boardCode: rate.rate.boardCode,
    refundable: rate.rate.refundable,
    type: 'bedbank',
    lpcRate: undefined,
    bedbankRate: rate,
  })))

  if (showAll) {
    return sortBy(allRates, (rate) => rate.price, 'asc')
  }

  // now dedupe them based on board code
  const ratesByCode = groupBy(allRates, rate => rate.boardCode ?? 'LE')
  const deDupedRates = Array.from(ratesByCode.entries()).flatMap(([boardCode, rateGroup]) => {
    if (boardCode === 'LE') {
      // this group is already unique, just pass them on
      return rateGroup
    }

    // now group again by cancellation policy
    const [refundableRates, nonRefundableRates] = partitionBy(rateGroup, group => group.refundable)
    // board codes describe common sets of inclusions, same board code = basically the same rate
    // so just return the cheapest one. In the event we have LPC and bedbank the same price
    // use the LPC one instead until we have margin info
    return nonNullable([
      min(sortBy(refundableRates, rate => rate.type === 'LPC' ? 1 : 0, 'desc'), rate => rate.price)!,
      min(sortBy(nonRefundableRates, rate => rate.type === 'LPC' ? 1 : 0, 'desc'), rate => rate.price)!,
    ])
  })

  return sortBy(deDupedRates, (rate) => rate.price, 'asc')
}

export function getPackageOptionsByRoom(
  packageOptions: Array<OfferPackageOptionView>,
  bedbankPackageViews: Array<App.BedbankOfferPackageView>,
  mapping?: App.PropertyOfferMapping,
) {
  // Prepare LPC and Bedbank package options by room
  const lpcPackagesByOpaqueRoomId = arrayToMap(
    buildRoomPackageOptionsViewOrderLPC(packageOptions), pkg => pkg.roomId, pkg => pkg.packageViews,
  )
  const bedbankRoomIdToOpaqueRoomId = new Map(Object.values(mapping?.rooms ?? {}).flatMap(
    ({ roomId, bedbankRoomIds }) => bedbankRoomIds.map((bedbankRoomId) => [bedbankRoomId, roomId])),
  )
  const bedbankPackagesByOpaqueRoomId = groupBy(
    bedbankPackageViews,
    // `mapping` (supplied by `svc-accommodation`) _only_ includes bedbank rooms (mapped to an
    // accommodation room) if there's also a reservation room mapped to that accommodation room.
    // However, we _don't_ want to filter out bedbank rates just because there's no equivalent
    // reservation room.
    //
    // Long-term, we should replace references to reservation/bedbank room IDs with accommodation
    // room IDs. In the interim, we'll pretend they're interchangable; as they're both UUIDs, we
    // needn't worry about collisions.
    view => bedbankRoomIdToOpaqueRoomId.get(view.package.roomId) ?? view.package.roomId,
  )

  // Compute set of rooms with any available package options
  const opaqueRoomIds = new Set([
    ...lpcPackagesByOpaqueRoomId.keys(),
    ...bedbankPackagesByOpaqueRoomId.keys(),
  ])

  // Combine LPC and Bedbank package options by room
  const views = [...opaqueRoomIds].flatMap((opaqueRoomId) => {
    const lpcPackages = lpcPackagesByOpaqueRoomId.get(opaqueRoomId) ?? []
    const bedbankPackages = bedbankPackagesByOpaqueRoomId.get(opaqueRoomId) ?? []

    if (lpcPackages.length || bedbankPackages.some(pkg => pkg.available)) {
      return {
        roomId: opaqueRoomId,
        lpc: lpcPackages,
        bedbank: bedbankPackages,
      }
    }

    return []
  })

  // Property page just always sorts rooms by best price
  return sortBy(views, view => {
    const allPrices = [
      ...view.lpc.filter(lpc => lpc.capacityValid && lpc.hotelAvailable && !!lpc.hasAvailableRates).map(lpc => lpc.memberPricing?.price || lpc.pricing.price || Number.MAX_SAFE_INTEGER),
      ...view.bedbank.filter(bedbank => bedbank.capacityValid && bedbank.available).map(bedbank => bedbank.cheapestRate?.totals.memberInclusive || bedbank.cheapestRate?.totals.inclusive || Number.MAX_SAFE_INTEGER),
      Number.MAX_SAFE_INTEGER,
    ]

    return min(allPrices)
  }, 'asc')
}

/**
 * Returns a key that will match a property rate list rate key for the view given
 */
export function getRateListKey(view: RateView): string {
  if (view.bedbankRate) {
    return view.bedbankRate.rate.id
  } else if (view.lpcRate) {
    const pkg = view.lpcRate.package
    return [pkg.roomRate!.id, pkg.roomRate?.group?.id, pkg.lePackageId].join('-')
  }
  return ''
}

export function isRoomRateUnmatched(
  roomRateId: string | undefined,
  {
    availableOptions,
    isHighlighted,
    utmContent,
  }: {
    availableOptions: Array<OfferPackageOptionView>
    isHighlighted?: boolean
    utmContent?: string
  },
): boolean {
  if (isHighlighted && roomRateId && availableOptions.length && utmContent === 'gha-lpc') {
    // if roomRateId is associated with user currency (eg: cb4ebd70-33d1-11ed-9609-ffb70b8fec99_USD ) remove the currency and extract the rate id for comparison
    const baseRateId = roomRateId?.split('_')[0]
    return availableOptions.some(option => option.package.roomRate?.id === baseRateId)
  }
  return false
}
